/**
 * @module useApp
 * App-wide singleton state: authentication status, current app context,
 * navigation drawer, and cached lookup options.
 *
 * `currentApp` tracks which microservice the user is currently using
 * ("sales", "marketing", "assets", etc.). It drives which navigation
 * items are shown and which API calls are made. Stored in localStorage so
 * it survives page refresh.
 *
 * Options (cities, locations, brands, platforms) are cached in localStorage
 * so the UI renders immediately on load with the last-known values, then
 * refreshes from the API in the background. This stale-while-revalidate
 * pattern prevents visible loading states on navigation.
 */

import { ref, computed, reactive } from "vue";
import api from "@/utils/api.js";

const isDev = import.meta.env.MODE === "development";

// ── Navigation state ──────────────────────────────────────────────────────────

const drawer = ref(false);

// Speed-dial menus in the app bar
const showMainMenu = ref(false);
const showReviewsMenu = ref(false);
const showPettyCashMenu = ref(false);
const showMenuMenu = ref(false);
const showContactsMenu = ref(false);
const showWarehouseMenu = ref(false);
const showDisputesMenu = ref(false);

// ── Auth state ────────────────────────────────────────────────────────────────

const isAuthenticated = ref(false);
const userRole = ref("user");

const token = localStorage.getItem("authToken");
const role = localStorage.getItem("userRole");
const user = ref(JSON.parse(localStorage.getItem("user")));

// isAdmin covers both admin and appadmin roles so permission checks don't need
// to enumerate both values everywhere in templates
const isAdmin = computed(() => ["admin", "appadmin"].includes(userRole.value));

if (token) {
  isAuthenticated.value = true;
  userRole.value = role || "user";
}

// ── Current app context ───────────────────────────────────────────────────────

const currentApp = ref(JSON.parse(localStorage.getItem("currentApp")));
const isSales = computed(() => currentApp.value === "sales");
const isReviews = computed(() => currentApp.value === "reviews");

// ── Lookup options ────────────────────────────────────────────────────────────

const options = reactive({
  countries: [{ name: "UAE" }],
  cities: [{ name: "Dubai" }, { name: "Al Ain" }],
  locations: [],
  brands: [],
  platforms: [],
});

/**
 * Load lookup options with stale-while-revalidate:
 * 1. Immediately restore cached options so the UI isn't empty
 * 2. Fetch fresh options from the API in the background
 * 3. Update the cache with the fresh data
 *
 * This means selects are populated instantly on page load, and any new
 * locations/brands added in Settings appear after the next fetch.
 */
const fetchOptions = async () => {
  const storedOptions = JSON.parse(localStorage.getItem("options"));
  if (storedOptions) {
    Object.assign(options, storedOptions);
  }
  try {
    const response = await api.accountingApi.get("/api/accounting/sales/options");
    Object.assign(options, response.data);
    localStorage.setItem("options", JSON.stringify(options));
  } catch (err) {
    console.error("Failed to fetch options:", err);
  }
};

// ── App metadata ──────────────────────────────────────────────────────────────

const title = "Pizza Felice LLC";
const salesTitle = "Sales";
const reviewsTitle = "Reviews";

export function useApp() {
  return {
    currentApp,
    isSales,
    isReviews,
    drawer,
    fetchOptions,
    options,
    isDev,
    user,
    title,
    salesTitle,
    reviewsTitle,
    isAdmin,
    isAuthenticated,
    userRole,
    showMainMenu,
    showReviewsMenu,
    showPettyCashMenu,
    showMenuMenu,
    showContactsMenu,
    showWarehouseMenu,
    showDisputesMenu,
  };
}
